// Putting the line breaks back into a card's text.
//
// Riot's gallery flattens them: not one of the 1191 printings carries a
// newline, so "[Hidden] (Hide now...) [Reaction] (Play on your turn...)"
// arrives as a single run. The printed card gives each of those keywords a
// line of its own, and read as one paragraph they run together.
//
// Pure and DOM-free so it can be tested against the whole card corpus, which is
// the only way to know a rule this fiddly does not mangle something.

/**
 * Splits card text the way the printed card breaks it.
 *
 * The one reliable landmark is the **reminder**: a keyword that explains itself
 * does so in parentheses immediately after. So a line ends where a reminder
 * ends -- and, for the same reason, a line *begins* at a keyword that carries
 * one, wherever in the text it appears.
 *
 * Both halves are needed. A keyword run is usually leading, but not always:
 * Dredge Up prints "Draw 1." on its own line and "[Flow 2] (You may play this
 * from your trash...)" on the next, and looking only at the front of the text
 * found no keyword at all and set the card as one paragraph. A keyword run that
 * begins a line is only recognised after a finished sentence, which is what
 * keeps "Give a unit [Shield 3]." from being torn in half.
 *
 * Breaking after the *keyword* instead is what goes wrong, because plenty of
 * things follow a keyword without belonging to a line of their own:
 *
 *   [Deflect] [2][R]: Double my Might this turn.
 *   [Equip] [3][A]. This ability's Energy cost is reduced by...
 *   [Deathknell][>] [Predict 2]. (When I die, ...)
 *
 * In the first two the brackets are a *cost*, and in the third the arrow makes
 * the keyword the head of a sentence. Splitting on the keyword cut all three
 * mid-clause and left a line starting with ":" or ".". So when a keyword run is
 * followed by anything other than its reminder, the run is part of the sentence
 * and the text is left alone.
 *
 * `isKeyword` is passed in rather than imported so the caller's palette stays
 * the single source of which words are keywords.
 */
export function cardTextLines(text: string, isKeyword: (word: string) => boolean): string[] {
  const KEYWORD = /^\[([A-Za-z][A-Za-z-]+)(\s+(?:\d+|X))?\]/;

  /**
   * One whole line, if `s` starts with a keyword run that explains itself.
   *
   * Null for anything else, which is the conservative half of the rule: a run
   * followed by a cost, a colon or prose belongs to the sentence around it.
   */
  const takeLine = (s: string): { line: string; rest: string } | null => {
    // A run of keywords, which may be comma-separated: "[Assault 2], [Shield 2]".
    let cursor = s;
    let run = '';
    // What separated the previous keyword from this one, kept from the step
    // that saw it: "[Assault 2], [Shield 2]" keeps its comma.
    let separator = '';
    for (;;) {
      const head = KEYWORD.exec(cursor);
      if (!head || !isKeyword(head[1]!.toLowerCase())) break;
      const after = cursor.slice(head[0]!.length);
      // "[Deathknell][>]" points at an effect, so it heads a sentence.
      if (/^\[>{1,2}\]/.test(after)) return null;
      run += (run ? separator : '') + head[0]!;
      const gap = /^\s*,\s*|^\s+/.exec(after);
      if (!gap) { cursor = after; break; }
      separator = gap[0].includes(',') ? ', ' : ' ';
      cursor = after.slice(gap[0].length);
    }
    if (!run) return null;

    // A keyword's cost belongs to the keyword: "[Flow] [2] (You may play this
    // from your trash...)" is one line, and stopping at the cost left Dredge Up
    // as a single paragraph. What must still *not* close a line is a cost
    // followed by anything but a reminder -- "[Deflect] [2][R]: Double my
    // Might" is a sentence, and the colon is the tell, not the cost.
    let cost = '';
    for (;;) {
      const pip = /^\s*(\[(?:\d+|[A-Za-z])\])/.exec(cursor);
      if (!pip) break;
      cost += pip[1]!;
      cursor = cursor.slice(pip[0]!.length);
    }

    // The line only closes on a reminder. Anything else after the run -- a
    // colon, prose -- means the keywords belong to the sentence.
    const trimmed = cursor.trimStart();
    if (!trimmed.startsWith('(')) return null;
    const close = trimmed.indexOf(')');
    if (close < 0) return null;

    const head = cost ? `${run.trimEnd()} ${cost}` : run.trimEnd();
    return {
      line: `${head} ${trimmed.slice(0, close + 1)}`,
      rest: trimmed.slice(close + 1).trimStart(),
    };
  };

  /**
   * Where a keyword line starts later in the text, or -1.
   *
   * Only ever at a sentence boundary. A keyword in the middle of a clause --
   * "Give a unit [Shield 3]" -- is part of that clause, and breaking at it
   * would leave a line ending on a verb with no object.
   */
  const startsLater = (s: string): number => {
    for (let i = 1; i < s.length; i++) {
      if (s[i] !== '[') continue;
      const before = s.slice(0, i).trimEnd();
      const last = before[before.length - 1];
      if (last !== '.' && last !== ')' && last !== '!' && last !== '?') continue;
      if (takeLine(s.slice(i))) return i;
    }
    return -1;
  };

  const lines: string[] = [];
  let rest = text.trim();

  for (;;) {
    const here = takeLine(rest);
    if (here) {
      lines.push(here.line);
      rest = here.rest;
      if (!rest) break;
      continue;
    }
    // Not at the front -- but a keyword may still open a later line, which is
    // how "Draw 1." keeps the line the printed card gives it.
    const at = startsLater(rest);
    if (at < 0) break;
    lines.push(rest.slice(0, at).trimEnd());
    rest = rest.slice(at);
  }

  if (rest) lines.push(rest);
  return lines.length > 0 ? lines : [text];
}
